import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloseOutlined } from "@ant-design/icons";
import { Modal, Tooltip } from "../../../design-system";
import { clsx } from "clsx";
import { Tour, TourButton } from "../../tour/Tour";
import { hasSeenTour, markTourSeen } from "../../tour/tourSeen";
import { INTAKE_TOUR_KEY, INTAKE_TOUR_STEPS } from "../../intake/intakeTourSteps";
import { GuidedIntakeView } from "../../intake/GuidedIntakeView";
import classes from "./ModalGuidedIntake.module.less";

interface Props {
    open: boolean;
    onClose: () => void;
    /** When set, resume this existing draft's session instead of creating new. */
    projectId?: number;
    /**
     * Called after a successful submit with the new project's id. The caller owns
     * what happens next (close the modal, refetch, navigate). Defaults to
     * navigating to the analysis page — used when resuming inside ProjectView,
     * where the caller must refetch so the now-active project stops rendering this
     * modal. NOT the same as onClose, which redirects to the Draft tab on cancel.
     */
    onSubmitted?: (projectId: number) => void;
}

export const ModalGuidedIntake: React.FC<Props> = ({
    open,
    onClose,
    projectId,
    onSubmitted,
}) => {
    const navigate = useNavigate();
    // Block closing while documents are still uploading — closing would orphan the
    // in-flight requests and leave the screen out of sync with what was saved.
    const [uploading, setUploading] = useState(false);

    const [tourOpen, setTourOpen] = useState(false);
    // This component outlives the dialog's open/close cycle; don't let a tour
    // left open resurface on the next open.
    useEffect(() => {
        if (!open) setTourOpen(false);
    }, [open]);

    const tourTimerRef = useRef<number>();
    useEffect(() => () => window.clearTimeout(tourTimerRef.current), []);

    // Auto-open the tour once, the first time a user opens Guided Intake —
    // when the wizard signals it's on screen, delayed a beat so the dialog's
    // enter animation settles before the tour measures its targets.
    const handleIntakeReady = () => {
        if (hasSeenTour(INTAKE_TOUR_KEY)) return;
        markTourSeen(INTAKE_TOUR_KEY);
        tourTimerRef.current = window.setTimeout(() => setTourOpen(true), 500);
    };

    const handleClose = () => {
        if (uploading) return;
        onClose();
    };

    const handleSubmitted = (submittedProjectId: number) => {
        if (onSubmitted) {
            onSubmitted(submittedProjectId);
        } else {
            navigate(`/project/${submittedProjectId}/analysis/1`);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            keyboard={!uploading}
            footer={null}
            width={1200}
            destroyOnClose
            maskClosable={false}
            closable={false}
            className={classes.modal}
            centered={false}
            style={{ top: 0, paddingBottom: 0 }}
            maskStyle={{ background: "#222937" }}
        >
            <div className={classes.body}>
                <div className={classes.header}>
                    <div className={classes.headerLeft}>
                        <span className={classes.title}>Guided Intake</span>
                    </div>
                    <div className={classes.headerRight}>
                        <TourButton
                            className={classes.tourButton}
                            onClick={() => setTourOpen(true)}
                        />
                        <Tooltip title={uploading ? "Uploading documents…" : undefined}>
                            <button
                                type="button"
                                className={clsx(
                                    classes.closeButton,
                                    uploading && classes.closeButtonDisabled
                                )}
                                aria-label="Close"
                                aria-disabled={uploading}
                                onClick={handleClose}
                            >
                                <CloseOutlined />
                            </button>
                        </Tooltip>
                    </div>
                </div>
                <GuidedIntakeView
                    projectId={projectId}
                    onSubmitted={handleSubmitted}
                    onUploadingChange={setUploading}
                    onReady={handleIntakeReady}
                />
                <Tour
                    steps={INTAKE_TOUR_STEPS}
                    open={tourOpen}
                    onClose={() => setTourOpen(false)}
                />
            </div>
        </Modal>
    );
};

export default ModalGuidedIntake;
